/**
 * PGLP | MONTE HERMOM
 * HOOK: usePrazoEngine
 * ------------------------------------------------------------
 * Encapsula a comunicação com a Engine de Prazo.
 * ============================================================
 */

import { useEffect, useRef, useState, useCallback } from 'react';

const API_BASE = '/api/imovel';
const HEARTBEAT_MS = 3 * 60 * 1000;

export default function usePrazoEngine({
  laudoId,
  ciclo = 1,
  faseCodigo,
  autoSessao = false,
  autoPolling = true,
  intervaloPolling = 60 * 1000,
}) {
  const [estado, setEstado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [sessaoId, setSessaoId] = useState(null);

  const heartbeatRef = useRef(null);
  const pollingRef   = useRef(null);

  const buscarEstado = useCallback(async (recalcular = false) => {
    if (!laudoId) return null;
    setLoading(true);
    setErro(null);
    try {
      const url = `${API_BASE}/prazo_status.php?laudo_id=${laudoId}&ciclo=${ciclo}${recalcular ? '&recalcular=1' : ''}`;
      const resp = await fetch(url, { credentials: 'include' });
      const data = await resp.json();

      if (!data.success) {
        setErro(data.erro || 'Falha ao consultar prazo');
        return null;
      }
      if (!data.existe) {
        setEstado(null);
        return null;
      }
      setEstado(data.estado);
      return data.estado;
    } catch (e) {
      setErro(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [laudoId, ciclo]);

  const abrirSessao = useCallback(async () => {
    if (!laudoId || !faseCodigo) return null;
    try {
      const resp = await fetch(`${API_BASE}/sessao_abrir.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          laudo_id: laudoId,
          ciclo,
          fase_codigo: faseCodigo,
        }),
      });
      const data = await resp.json();
      if (data.success && data.sessao_id) {
        setSessaoId(data.sessao_id);
        return data.sessao_id;
      }
      return null;
    } catch {
      return null;
    }
  }, [laudoId, ciclo, faseCodigo]);

  const encerrarSessao = useCallback(async (id = sessaoId, motivo = 'MANUAL') => {
    if (!id) return false;
    try {
      await fetch(`${API_BASE}/sessao_encerrar.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessao_id: id, motivo }),
      });
      setSessaoId(null);
      return true;
    } catch {
      return false;
    }
  }, [sessaoId]);

  const enviarHeartbeat = useCallback(async (id = sessaoId) => {
    if (!id) return false;
    try {
      await fetch(`${API_BASE}/sessao_heartbeat.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessao_id: id }),
      });
      return true;
    } catch {
      return false;
    }
  }, [sessaoId]);

  const registrarProvidencia = useCallback(async (acao, payload = {}) => {
    try {
      const resp = await fetch(`${API_BASE}/prazo_acao.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ laudo_id: laudoId, ciclo, acao, payload }),
      });
      const data = await resp.json();
      if (data.success) {
        await buscarEstado(true);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [laudoId, ciclo, buscarEstado]);

  const prorrogarPrazo = useCallback(async (prorrogacao) => {
    try {
      const resp = await fetch(`${API_BASE}/prazo_prorrogar.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ laudo_id: laudoId, ciclo, prorrogacao }),
      });
      const data = await resp.json();
      if (data.success) {
        await buscarEstado(false);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [laudoId, ciclo, buscarEstado]);

  useEffect(() => {
    if (!autoSessao || !laudoId || !faseCodigo) return undefined;
    let sessaoAtual = null;
    (async () => {
      sessaoAtual = await abrirSessao();
      if (sessaoAtual) {
        heartbeatRef.current = setInterval(() => {
          enviarHeartbeat(sessaoAtual);
        }, HEARTBEAT_MS);
      }
    })();
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (sessaoAtual) encerrarSessao(sessaoAtual, 'TROCA_FASE');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSessao, laudoId, faseCodigo]);

  useEffect(() => {
    if (!autoPolling || !laudoId) return undefined;
    buscarEstado(false);
    pollingRef.current = setInterval(() => {
      buscarEstado(false);
    }, intervaloPolling);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [autoPolling, laudoId, intervaloPolling, buscarEstado]);

  return {
    estado,
    loading,
    erro,
    sessaoId,
    buscarEstado,
    abrirSessao,
    encerrarSessao,
    enviarHeartbeat,
    registrarProvidencia,
    prorrogarPrazo,
  };
}